import './index.css'
import cn from 'clsx'
import * as React from 'react'
type Props = {
  style?: Record<string, any>
  children: React.ReactNode
  textAlign?: 'left' | 'center' | 'right' | 'justify'
  theme: 'default' | 'light'
  className?: string
  color?: string
  display?: string
  title?: string
  type:
    | 'H900'
    | 'H800'
    | 'H700'
    | 'H600'
    | 'H500'
    | 'H400'
    | 'H300'
    | 'H200'
    | 'H100'
    | 'paragraph'
    | 'text-link'
    | 'block-quote'
    | 'small'
    | 'code'
}

/**
 * @deprecated Use `Text` or `Heading` from `components/ds` instead.
 * @see {@link components/ds/Text}
 * @see {@link components/ds/Heading}
 * @migration
 * - For heading types (H900-H100), use `Heading` from components/ds:
 *   - type="H900" → `<Heading size="9">`
 *   - type="H800" → `<Heading size="8">`
 *   - type="H700" → `<Heading size="7">`
 *   - type="H600" → `<Heading size="6">`
 *   - type="H500" → `<Heading size="5">`
 *   - type="H400" → `<Heading size="4">`
 *   - type="H300" → `<Heading size="3">`
 *   - type="H200" → `<Heading size="2">`
 *   - type="H100" → `<Heading size="1">`
 * - For body text types, use `Text` from components/ds:
 *   - type="paragraph" → `<Text size="2">`
 *   - type="small" → `<Text size="1">`
 *   - type="code" → `<Text as="code">`
 * - theme="light" → use `color="gray"` on DS Text/Heading
 * - textAlign → use `align` prop on DS Text/Heading
 * - style prop is not supported; use CSS Modules or Radix layout components
 *
 * A versatile text component with multiple typography styles and themes
 * @param type - The text type/style (H900-H100, paragraph, text-link, block-quote, small, code)
 * @param theme - The color theme: 'default' or 'light' (defaults to 'default')
 * @param className - Additional CSS classes to apply
 * @param children - The text content to display
 * @param style - Inline styles to apply
 * @param title - Title attribute for accessibility
 * @param textAlign - Text alignment: 'left', 'center', 'right', or 'justify' (defaults to 'left')
 * @param color - Custom text color
 * @param display - CSS display property
 * @returns A span element with typography styling and theming
 */
const Text = ({
  type,
  theme,
  className,
  children,
  style,
  title,
  ...rest
}: Props) => {
  const st = { ...style, ...rest }
  const cnTheme = `text--${theme}`
  const cln = cn(`text--${type}`, !rest.color && cnTheme, className)
  return (
    <span title={title} className={cln} style={st}>
      {children}
    </span>
  )
}

Text.defaultProps = {
  theme: 'default',
  textAlign: 'left',
}
export default React.memo(Text)
